/* eslint-disable react-hooks/set-state-in-effect --
   Open/close + keyboard-nav effects sync UI state with the palette
   lifecycle (focus, recent pages). The effects' setState calls are
   the canonical "subscribe to UI lifecycle" pattern. */
// frontend/src/hooks/useCommandPalette.js
// Manages command palette state: open/close, search, keyboard nav, recent pages.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import APP_PAGES, { APP_ACTIONS, CATEGORIES } from '../config/appPages';
import { getTitleKeyForPath } from '../tabs/routeRegistry';
import { useTabs } from '../tabs/useTabs';
import { useAuth } from '../contexts/AuthContext';
import { useInAppSyncStatus } from './useInAppSyncStatus';
import { forecastsMissing } from '../utils/syncGate';
import { trackEvent } from './useTracking';

const SEARCH_EVENT_TYPE = 'command_palette_search';
const MIN_LOGGED_QUERY_LEN = 2;

const MAX_RECENT = 5;
const RECENT_STORAGE_KEY = 'commandPalette_recent';

// Category display order
const CATEGORY_ORDER = [
  CATEGORIES.NAVIGATION,
  CATEGORIES.PURCHASE,
  CATEGORIES.REPORTS,
  CATEGORIES.SETTINGS,
  CATEGORIES.TOOLS,
  CATEGORIES.ACCOUNT,
  CATEGORIES.ACTIONS,
];

// Category label keys
const CATEGORY_LABELS = {
  [CATEGORIES.NAVIGATION]: 'commandPalette.categories.navigation',
  [CATEGORIES.PURCHASE]: 'commandPalette.categories.purchase',
  [CATEGORIES.REPORTS]: 'commandPalette.categories.reports',
  [CATEGORIES.SETTINGS]: 'commandPalette.categories.settings',
  [CATEGORIES.TOOLS]: 'commandPalette.categories.tools',
  [CATEGORIES.ACCOUNT]: 'commandPalette.categories.account',
  [CATEGORIES.ACTIONS]: 'commandPalette.categories.actions',
};

function getRecent() {
  try {
    return JSON.parse(sessionStorage.getItem(RECENT_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function addRecent(path) {
  const recent = getRecent().filter((p) => p !== path);
  recent.unshift(path);
  sessionStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

// Simple fuzzy match: check if all query words appear in the target string
function fuzzyMatch(query, target) {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const lowerTarget = target.toLowerCase();
  return words.every((w) => lowerTarget.includes(w));
}

// Score a result: exact prefix match scores highest, then keyword match
function scoreResult(query, label, keywords) {
  const q = query.toLowerCase();
  const l = label.toLowerCase();

  // Exact start match (highest priority)
  if (l.startsWith(q)) return 100;
  // Word-boundary match within label
  if (l.includes(q)) return 80;
  // Keyword exact match
  if (keywords.some((k) => k.startsWith(q))) return 60;
  // Keyword partial match
  if (keywords.some((k) => k.includes(q))) return 40;
  // Fuzzy across label + keywords combined
  const combined = `${l} ${keywords.join(' ')}`;
  if (fuzzyMatch(query, combined)) return 20;

  return 0;
}

export default function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const { openTab } = useTabs();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { userContext } = useAuth();
  const isStaff = userContext.user.is_staff;
  const forecastOffered = !forecastsMissing(useInAppSyncStatus());
  const inputRef = useRef(null);
  // One tracking event per palette-open session. Set when emitted so
  // that closing after a selection doesn't double-log.
  const loggedForSessionRef = useRef(false);
  // Latest query + visible-result-count, captured for the close path.
  const latestSearchRef = useRef({ query: '', resultCount: 0 });

  // Track page visits for recent
  useEffect(() => {
    const page = APP_PAGES.find((p) => p.path === location.pathname);
    if (page) addRecent(page.path);
  }, [location.pathname]);

  // Ctrl+K / Cmd+K toggle. Escape is the popup's own (the palette closes
  // through its onClose, as every popup does).
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      loggedForSessionRef.current = false;
      latestSearchRef.current = { query: '', resultCount: 0 };
      // Focus input after render
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Build all searchable entries (translated labels), filtering staff-only
  // pages, and the pages that need a forecast while the store has none.
  const allEntries = useMemo(() => {
    const visiblePages = APP_PAGES.filter((p) => (isStaff || !p.staffOnly)
      && (forecastOffered || !p.needsForecast));
    const visibleActions = isStaff ? APP_ACTIONS : APP_ACTIONS.filter((a) => !a.staffOnly);
    const pages = visiblePages.map((p) => ({
      ...p,
      id: p.path,
      label: t(getTitleKeyForPath(p.path)),
      description: t(p.descriptionKey),
      type: 'page',
    }));
    const actions = visibleActions.map((a) => ({
      ...a,
      label: t(a.labelKey),
      description: '',
      type: 'action',
    }));
    return [...pages, ...actions];
  }, [t, isStaff, forecastOffered]);

  // Filter and score results
  const results = useMemo(() => {
    const trimmed = query.trim();

    if (!trimmed) {
      // Show recent pages when no query
      const recentPaths = getRecent();
      const recentEntries = recentPaths
        .map((path) => allEntries.find((e) => e.id === path))
        .filter(Boolean);
      return { recent: recentEntries, grouped: [] };
    }

    // Score all entries
    const scored = allEntries
      .map((entry) => ({
        ...entry,
        score: scoreResult(trimmed, entry.label, entry.keywords),
      }))
      .filter((e) => e.score > 0)
      .sort((a, b) => b.score - a.score);

    // Group by category
    const groupMap = new Map();
    for (const entry of scored) {
      if (!groupMap.has(entry.category)) {
        groupMap.set(entry.category, []);
      }
      groupMap.get(entry.category).push(entry);
    }

    // Sort groups by category order
    const grouped = CATEGORY_ORDER
      .filter((cat) => groupMap.has(cat))
      .map((cat) => ({
        category: cat,
        label: t(CATEGORY_LABELS[cat]),
        items: groupMap.get(cat),
      }));

    return { recent: [], grouped };
  }, [query, allEntries, t]);

  // Flat list for keyboard navigation
  const flatResults = useMemo(() => {
    if (results.recent.length > 0) return results.recent;
    return results.grouped.flatMap((g) => g.items);
  }, [results]);

  // Track the latest query + result count so close() can log the final
  // state without re-subscribing the callback on every keystroke.
  useEffect(() => {
    const trimmed = query.trim();
    // recent-view isn't a search — only count result groups
    const count = results.grouped.reduce((n, g) => n + g.items.length, 0);
    latestSearchRef.current = { query: trimmed, resultCount: count };
  }, [query, results]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const selectItem = useCallback(
    (entry) => {
      if (!entry) return;
      // Log the successful selection before closing — the close-effect
      // below will see loggedForSessionRef=true and skip the no-select log.
      if (!loggedForSessionRef.current) {
        const { query: q, resultCount } = latestSearchRef.current;
        trackEvent(SEARCH_EVENT_TYPE, {
          query: q,
          result_count: resultCount,
          selected_path: entry.path,
          language: i18n.language?.slice(0, 5) || null,
        });
        loggedForSessionRef.current = true;
      }
      addRecent(entry.path);
      // Tab opener (focus-or-create), not raw navigate() — selecting the
      // page you're already on would otherwise be a no-op and open nothing.
      openTab(entry.path);
      setIsOpen(false);
    },
    [openTab, i18n],
  );

  // Emit an abandoned-search event whenever the palette closes with a
  // meaningful query that wasn't followed by a selection. Runs regardless
  // of how close happened — Escape key, Ctrl+K toggle, click-outside, or
  // explicit close() — so logging can't be bypassed by any close path.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen && !loggedForSessionRef.current) {
      const { query: q, resultCount } = latestSearchRef.current;
      if (q.length >= MIN_LOGGED_QUERY_LEN) {
        trackEvent(SEARCH_EVENT_TYPE, {
          query: q,
          result_count: resultCount,
          selected_path: null,
          language: i18n.language?.slice(0, 5) || null,
        });
        loggedForSessionRef.current = true;
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, i18n]);

  const handleKeyDown = useCallback(
    (e) => {
      if (flatResults.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % flatResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + flatResults.length) % flatResults.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectItem(flatResults[activeIndex]);
      }
    },
    [flatResults, activeIndex, selectItem],
  );

  return {
    isOpen,
    open,
    close,
    query,
    setQuery,
    results,
    flatResults,
    activeIndex,
    setActiveIndex,
    selectItem,
    handleKeyDown,
    inputRef,
  };
}
